import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form100SInstruction = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 100S Instructions 2025 | California S Corporation"
          description="Explore Form 100S Instructions 2025 for California S corporations. Find filing guidance, due dates, tax information, and FTB requirements."
          keywords=""
          canonical="resources/business-tax/form-100s-instructions/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          California{' '}
          <span className="highlight-container">
            <span className="highlight"> Form 100S</span>
          </span>{' '}
          Instructions: Complete{' '}
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          Filing Guide for S Corporations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Prepare and e-file your California Form 100S accurately with TaxZerone
          <Hidden smDown>
            {' '}
            <br />{' '}
          </Hidden>
          and stay compliant with California FTB filing requirements.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=100sinstructions`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  component="article"
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/business-tax/form-100s-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-25T09:00:00-05:00"
                      >
                        Aug 25, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-25T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 25, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    component="h2"
                    itemProp="headline"
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 0.8,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    California Form 100S at a Glance
                  </Typography>
                  <Typography
                    component="p"
                    itemProp="description"
                    variant="body1"
                  >
                    <strong style={{ fontWeight: 600 }}> Form 100S </strong> is
                    filed by California S corporations with the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Franchise Tax Board (FTB)
                    </strong>{' '}
                    to report income, deductions, credits, and tax information.
                    S corporation income and other tax items generally pass
                    through to shareholders.
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Due Date: </strong>
                        The filing deadline depends on the corporation’s tax
                        year and follows applicable California and federal
                        filing requirements.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}> Extension:</strong>{' '}
                        S corporations can request an extension to file Form
                        100S. The extension provides additional filing time but
                        does not extend the time to pay taxes owed.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Late Filing Penalties:
                        </strong>{' '}
                        The FTB may charge penalties for late filing, late
                        payment, or incorrect or incomplete return information.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/business-tax/efile-california-form-100s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    CA Form 100S
                  </Link>{' '}
                  is the official California income tax return for S
                  corporations. If your business is taxed as an S corporation in
                  California, you must file Form 100S each year with the
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    California Franchise Tax Board (FTB)
                  </span>{' '}
                  to report income, deductions, franchise tax, shareholder
                  distributions, and tax adjustments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This comprehensive guide walks you through every part of
                  California Form 100S with step-by-step instructions, filing
                  requirements, due dates, required schedules, and practical
                  examples. Whether you're filing for the first time or need
                  help completing a specific section, you'll find clear
                  explanations, common filing mistakes to avoid, and official
                  guidance to help you prepare and file your return accurately
                  and confidently.
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatnew" sx={{ color: '#0202ff' }}>
                          What’s New about California Form 100S?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What Is California Form 100S?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who Must File Form 100S?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Important Filing Deadlines for Form 100S
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#tax" sx={{ color: '#0202ff' }}>
                          California S Corporation Tax Rates
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Schedules and Attachments
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#adjust" sx={{ color: '#0202ff' }}>
                          California Adjustments and Apportionment
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#line" sx={{ color: '#0202ff' }}>
                          Line-by-Line Instruction for Form 100S
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Form 100S Filing Mistakes
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalt" sx={{ color: '#0202ff' }}>
                          Penalties and Interest for Form 100S
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#docu" sx={{ color: '#0202ff' }}>
                          Documents Needed Before Filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#tips" sx={{ color: '#0202ff' }}>
                          Final Filing Tips
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#faq" sx={{ color: '#0202ff' }}>
                          Frequently Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatnew"
                >
                  What's new about California Form 100S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  S Corporations Filing Form 100S for California 2025 Tax
                  Returns: Important Updates
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Report on Form FTB 4197:{' '}
                    </span>{' '}
                    <br />
                    Certain taxpayers must declare tax spending items on Form
                    FTB 4197, Information on Tax Spending Items, as required by
                    California law.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      OBBBA Nonconformity:
                    </span>{' '}
                    <br />
                    California generally does not conform to federal tax changes
                    under the One Big Beautiful Bill Act (OBBBA).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Motion Picture Credit Program 4.0:
                    </span>{' '}
                    <br />
                    New California Motion Picture and Television Production
                    Credit A new California Motion Picture and Television
                    Production Credit is available for taxable years beginning
                    on or after January 1, 2025. An S Corporation doesn’t claim
                    the credit itself instead, the full amount of the credit
                    passes through to the shareholders.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      PTE Elective Tax Extended:
                    </span>{' '}
                    <br />
                    The PTE elective tax and credit program has been extended
                    for taxable years beginning on or after January 1, 2026, and
                    before January 1, 2031. Qualified PTEs that do not make or
                    underpay the June 15 prepayment may still make the election;
                    however, the related credit is reduced by 12.5% of the
                    taxpayer’s share of the unpaid amount. But the related
                    credit is reduced by 12.5 percent of the taxpayer's portion
                    of the unpaid amount. taxpayer's share of the unpaid amount.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Changes in Single Sales Factor Apportionment:
                    </span>{' '}
                    <br />
                    Effective in 2025, banking, savings and loan, and other
                    financial business activities will no longer be treated as
                    qualified business activities for California's single sales
                    factor apportionment.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Wildfire Settlement Exclusion:
                    </span>{' '}
                    <br />
                    California offers an exclusion from gross income for
                    qualifying wildfire disaster settlement payments received
                    during the period from 2021 to 2030. Eligible taxpayers can,
                    if applicable, file amended returns for prior years.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Chiquita Canyon Exclusion:
                    </span>{' '}
                    <br />
                    California removes certain payments from the Chiquita Canyon
                    dump event from total income for taxable years beginning
                    after 2023 and before 2030. If applicable, eligible
                    taxpayers may alter prior year returns.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatis"
                >
                  What Is California Form 100S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <Link
                    href="/business-tax/efile-california-form-100s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    California Form 100S
                  </Link>{' '}
                  is the tax return filed by S corporations conducting business
                  in California. The form reports:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Business income and losses </li>
                  <li>California franchise tax </li>
                  <li>Dividends to shareholders </li>
                  <li>Deductions and credits </li>
                  <li>California adjustments </li>
                  <li>Apportioned income for multi-state businesses </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The form is filed with the California Franchise Tax Board
                  (FTB) and is generally due on the 15th day of the third month
                  after the close of the tax year.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Need Help Filing California Form 100S?
                      </Typography>
                      <Typography variant="body2">
                        File your California S corporation return correctly and
                        on schedule to avoid penalties and filing errors.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=100sinstructions`}
                      >
                        File Form 100s Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who Must File Form 100S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  An S corporation is required to file CA Form 100S if it:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Registered in California</li>
                  <li>Conducts business in California </li>
                  <li>California-source income earned </li>
                  <li>
                    Has elected S-corporation status for federal purposes{' '}
                  </li>
                  <li>
                    Is incorporated or qualified with the California Secretary
                    of State{' '}
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Inactive corporations may still have filing
                  requirements until they are formally dissolved.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="filing"
                >
                  Important Filing Deadlines for Form 100S
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Filing Requirement
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Original Return </TableCell>
                          <TableCell>15th day of 3rd month</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Extended Return </TableCell>
                          <TableCell>15th day of 9th month </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Estimated Tax Payments </TableCell>
                          <TableCell>
                            15th day of the 4th, 6th, 9th, and 12th month{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Corporations must pay the required percentage of their
                  estimated tax liability during the taxable year as specified
                  below.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>30% for the first required installment </li>
                  <li>40% for the second required installment </li>
                  <li>
                    The third installment does not require an estimated tax
                    payment{' '}
                  </li>
                  <li>30% for the fourth required installment</li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Tax must still be paid by the original due date
                  to avoid penalties and interest.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  California S Corporation Tax Rates
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  California S corporations Typically pay:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Income / Franchise Tax at 1.5% </li>
                  <li>Minimum franchise tax of $800 </li>
                  <li>Financial S corps pay 3.5% </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This minimal franchise tax due even if the corporation has no
                  income, unless a specified exemption applies.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="key"
                >
                  Key Schedules and Attachments
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Required Schedule/Attachment
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Purpose
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Schedule D </TableCell>
                          <TableCell>
                            Reports gains and losses from sales of capital
                            assets
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Schedule B </TableCell>
                          <TableCell>
                            Reports depreciation and amortization{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Schedule C </TableCell>
                          <TableCell>Claims California tax credits </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{}}>Schedule H</TableCell>
                          <TableCell sx={{}}>
                            Reports deductions for dividends
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Schedule R </TableCell>
                          <TableCell>
                            Calculates California apportionment income{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{}}>Schedule F</TableCell>
                          <TableCell sx={{}}>
                            Calculation of Trade or Business Income
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Schedule L </TableCell>
                          <TableCell>
                            Reports balance sheet information, if required{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{}}>Schedule M-1</TableCell>
                          <TableCell sx={{}}>
                            Reconciles book income and tax income, if required
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Schedule M-2 </TableCell>
                          <TableCell>
                            Reports accumulated adjustments and retained
                            earnings{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{}}>Schedule V</TableCell>
                          <TableCell sx={{}}>
                            Calculates cost of goods sold
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Schedule K </TableCell>
                          <TableCell>
                            Summarizes shareholder income, deductions, and
                            credits{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{}}>Schedule K-1</TableCell>
                          <TableCell sx={{}}>
                            Reports each shareholder’s share of income,
                            deductions, credits, and other tax items
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            Federal{' '}
                            <Link
                              href="/resources/business-tax/form-1120-s-instructions/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Form 1120-S
                            </Link>{' '}
                          </TableCell>
                          <TableCell>
                            Provides the federal S corporation return copy
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Additional schedules/supporting statements may
                  be required depending on business activities.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  How These Schedules Work:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Schedules attached to California Form 100S assist in the
                    correct reporting of shareholder income, deductions, tax
                    adjustments, credits, and other business activities
                    accurately.
                  </li>
                  <li>
                    Fill out each schedule carefully. Please attach them in the
                    proper order to help avoid processing delays, notices, or
                    filing errors.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="adjust"
                >
                  California Adjustments and Apportionments
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  California does not always follow federal tax rules. For Form
                  100S, a business may be required to make California-specific
                  adjustments.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '16px !important',
                  }}
                >
                  Common adjustments include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Depreciation differences </li>
                  <li>Franchise tax adjustments </li>
                  <li>Passive activity limitations </li>
                  <li>State-specific deductions </li>
                  <li>Built-in gains adjustments </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Multi-state enterprises may also required to file Schedule R
                  to apportion income earned both inside and out of California.
                  Many businesses in California adopt a single sales factor
                  allocation technique.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="line"
                >
                  Line-by-Line Instructions for Form 100S
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Let’s now explore the line-by-line instructions for form 100S.
                  This section provides a detailed summary of your corporation’s
                  financial activity and tax reporting.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                  id="basic"
                >
                  Basic Information Section
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-basic-info-img.webp" />
                  <source srcSet="/assets/images/100s-basic-info-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-basic-info-img.webp"
                    loading="lazy"
                    alt="Basic Information Section (Top of the Form)"
                    width={757}
                    height={250}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Tax Year</span> – Enter
                    the beginning and ending dates of the corporation’s tax
                    year.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Corporation Name and Address Information
                    </span>{' '}
                    – Enter the corporation’s true legal name as filed with the
                    California SOS, including with the street address, city,
                    state, and ZIP code.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      California Corporation Number
                    </span>
                    – Enter the California corporation number.
                  </li>{' '}
                  Enter your
                  <li>
                    <span style={{ fontWeight: 600 }}>FEIN</span> – Enter your
                    Federal Employer Identification Number (FEIN).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Additional Information
                    </span>{' '}
                    – Use this field for owner, representative, or supple mental
                    address information only.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Secretary of State File Number
                    </span>{' '}
                    – Type the file number from the California Secretary of
                    State.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>PMB Number</span> – Enter
                    the private mailbox number, if applicable.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Foreign Address</span> –
                    Enter foreign country, province/state/county, and postal
                    code if the corporation is located outside the U.S.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="income"
                >
                  Schedule Q Questions
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule Q is intended to disclose crucial informatio n
                  regarding the corporation’s business activities, changes in
                  ownership, deferred income and other California filing
                  requirements. Please answer all appropriate questions in the
                  following pages, and send any extra information or attachments
                  as necessary.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-q-questions-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-q-questions-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-q-questions-img.png"
                    loading="lazy"
                    alt="Schedule Q Questions"
                    width={759}
                    height={320}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Question A1 - Final Return Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If this is the corporation's final return, check the box and
                  state the reason:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Dissolved</span> –
                    Corporation has been legally closed
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Surrendered (Withdrawn)
                    </span>{' '}
                    – Business has officially withdrawn from California
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Merged/Reorganized</span>{' '}
                    – Corporation merged into another entity or reorganized
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      IRC Section 338 sale
                    </span>{' '}
                    – Stock purchase treated as asset sale under tax rules
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>QSub election</span> –
                    Subsidiary treated as a disregarded entity
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>date of final action</span>{' '}
                  (mm/dd/yyyy).
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                  Question A2 - Deferral of Income from Asset Disposition
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Indicate whether the S corporation is currently deferring
                  income from the sale or exchange of assets.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Check <span style={{ fontWeight: 600 }}>Yes</span> – If you
                    are deferring income under the tax rules.
                  </li>

                  <li>
                    Check <span style={{ fontWeight: 600 }}>No</span> – If no
                    deferred income exists.
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If “Yes”, indicate the year of first disposal of the asset
                  (yyyy).
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                  Question A3 - Previously Deferred Income Reporting
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Indicate whether the S corporation is reporting previously
                  delayed income from any of the following:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Installment sale</li>
                  <li>IRC  §1031 Like-kind exchange</li>
                  <li>IRC  §1033 Involuntary conversion</li>
                  <li>Other deferred income transactions</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check all applicable boxes that apply to the income being
                  reported.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                  Question B1 - Acquisition of Control (Buying Another Entity)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Answer whether the corporation (or any subsidiary) acquired{' '}
                  <span style={{ fontWeight: 600 }}>
                    more than 50% ownership/control
                  </span>{' '}
                  of another entity during the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If <span style={{ fontWeight: 600 }}>yes</span>, also check
                  whether the acquired entity owned or leased leased rnia real
                  property such as:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Property or buildings</li>
                  <li>Lease for 35+ years</li>
                  <li>Government-Owned property</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If both conditions are true  †’ mark{' '}
                  <span style={{ fontWeight: 600 }}>YES</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Otherwise  †’ mark <span style={{ fontWeight: 600 }}>NO</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                  Question B2 - Change in Ownership of This Corporation (Being
                  Acquired)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Answer whether another person or entity acquired{' '}
                  <span style={{ fontWeight: 600 }}>
                    more than 50% ownership/control
                  </span>{' '}
                  of this corporation (or its subsidiaries). If YES, also check
                  whether:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If <span style={{ fontWeight: 600 }}>YES</span>, also check
                  whether:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    The acquired entity owned California real property, or
                  </li>
                  <li>Held qualifying long-term or government leases</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If both conditions are true  †’ mark{' '}
                  <span style={{ fontWeight: 600 }}>YES</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Otherwise  †’ mark <span style={{ fontWeight: 600 }}>NO</span>
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                  Question B3 - Real Property Transfer & Reassessment Rules
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check if California real property was transferred into the
                  company and shielded from property tax reassessment under RTC
                  Section 62(a)(2).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If <span style={{ fontWeight: 600 }}>YES</span>, also check
                  whether:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    More than{' '}
                    <span style={{ fontWeight: 600 }}>50% of voting stock</span>{' '}
                    of the corporation was transferred cumulatively in one or
                    more transactions during the year
                  </li>
                  <li>
                    And this transfer was{' '}
                    <span style={{ fontWeight: 600 }}>
                      not previously reported
                    </span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If both conditions are true  †’ mark{' '}
                  <span style={{ fontWeight: 600 }}>YES</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Otherwise  †’ mark <span style={{ fontWeight: 600 }}>NO</span>
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> If you answer “Yes,” you must file Form
                  BOE-100-B. Failure to file may result in penalties.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="section"
                >
                  State Adjustments (Line 1 - 4)
                </Typography>

                <picture>
                  <source srcSet="/assets/images/100s-state-adjustments-img.webp" />
                  <source srcSet="/assets/images/100s-state-adjustments-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-state-adjustments-img.png"
                    loading="lazy"
                    alt="State Adjustments (Line 1 - 4)"
                    width={771}
                    height={230}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1 – Ordinary Income (Loss) From Trade or Business
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s ordinary business income or loss from{' '}
                  <span style={{ fontWeight: 600 }}>
                    Schedule F (Form 100S), line 22, or federal Form 1120-S,
                    line 22.
                  </span>{' '}
                  If Schedule F is not completed, attach federal{' '}
                  <span style={{ fontWeight: 600 }}>Form 1120-S page 1</span>{' '}
                  along with supporting schedules and statements. The CEO’s
                  salary is $80,000.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2 – Taxes Not Deductible
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report foreign or domestic income taxes, including California
                  franchise or income taxes, that are not deductible and must be
                  added back to income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3 – Interest on Government Obligations
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report interest from government obligations that must be added
                  to income, even if exempt from federal or state individual
                  income tax.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4 – Net Capital Gain
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report net capital gains from the sale, exchange, or other
                  disposition of business assets. Include gains reported on{' '}
                  <span style={{ fontWeight: 600 }}>Schedule D (100S)</span> and
                  any{' '}
                  <span style={{ fontWeight: 600 }}>
                    IRC Section 179 recapture gains
                  </span>{' '}
                  required for California tax purposes. Enter the taxable gain
                  amount to{' '}
                  <span style={{ fontWeight: 600 }}>Form 100S, Line 4.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5 – Depreciation and Amortization
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Apply California depreciation rules to report depreciation and
                  amortization adjustments for qualifying business assets.
                  Attach Schedule B (Form 100S) to support the amounts reported.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6 – Portfolio Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report net portfolio income, such as interest, dividends, and
                  royalties, that is not included in ordinary business income.
                  Do not include passive activity income on this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7 – Other Additions
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter income and adjustment items not reported elsewhere
                    that must be added to California net income under R&TC
                    Section 24425.{' '}
                  </li>
                  <li>
                    Include expenses allocable to income not included in
                    California taxable income (add-back items).
                  </li>
                  <li>
                    Please also report any other adjustments necessary to
                    accurately compute California net income.{' '}
                  </li>
                  <li>
                    Attach an itemized schedule showing what the item is, how it
                    was calculated, and why it was adjusted.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 8 – Total
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 1 through 7 to determine total income and
                  adjustments.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  State Adjustments (Line 9 - 14)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-state-adjustments-l-9-14-img.webp" />
                  <source srcSet="/assets/images/100s-state-adjustments-l-9-14-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-state-adjustments-l-9-14-img.png"
                    loading="lazy"
                    alt="State Adjustments (Line 1 - 4)"
                    width={771}
                    height={140}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 9 – Dividends Received Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the dividends received deduction and attach Schedule H
                  (100S).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 10 – Water’s-Edge Dividend Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the water’s-edge dividend deduction and attach Schedule
                  H (100S).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 11 – Charitable Contributions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report allowable charitable contributions subject to
                  California limitations. Complete the required calculation and
                  use Schedule R for apportioning corporations, if applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12 – Other Deductions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report deductions not claimed elsewhere, such as pass-through
                  losses, passive activity losses, IRC Section 179 expenses,
                  federal ordinary gains or losses from Form 4797, exempt
                  government interest, and eligible California exclusions.
                  Attach supporting schedules and applicable forms, such as
                  Schedule B (100S) and Form 8825, if necessary.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 13 – Total
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 9 through 12 to determine total deductions and
                  contribution.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 14 – Net Income (Loss) After State Adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s California net income or loss after
                  subtracting the total deductions and adjustments on Line 13
                  from the total income reported on Side 1, Line 8.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$120,000</span> on Line 8
                  and <span style={{ fontWeight: 600 }}>$20,000</span>
                  on Line 13, resulting in{' '}
                  <span style={{ fontWeight: 600 }}>$100,000</span> of
                  California net income.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  CA Net Income (Line 15 – 20)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-ca-net-income-l-15-20-img.webp" />
                  <source srcSet="/assets/images/100s-ca-net-income-l-15-20-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-ca-net-income-l-15-20-img.png"
                    loading="lazy"
                    alt="CA Net Income (Line 15 – 20)"
                    width={771}
                    height={180}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15 – Net Income (Loss) for State Purposes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the corporation’s California net income or loss. Use
                  Schedule R if income is earned both inside and outside
                  California and attach
                  <span style={{ fontWeight: 600 }}>Form FTB 3805Q</span> if
                  reporting a net loss. Corporations qualifying under
                  <span style={{ fontWeight: 600 }}>Public Law 86-272</span> may
                  be exempt from California income tax but could still be
                  subject to the minimum franchise tax.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 16 – R&TC Section 23802(e) Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the allowable deduction under R&TC Section 23802(e) for
                  excess net passive investment income or built-in gains tax, if
                  applicable. Use the Excess Net Passive Income and Income Tax
                  Worksheet or Schedule D (100S), Section A, Part III, to
                  calculate the deduction amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line 17 – Net Operating Loss (NOL) Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Claim any allowable NOL carryover from prior years. Complete
                  and attach{' '}
                  <span style={{ fontWeight: 600 }}>Form FTB 3805Q</span> to
                  calculate the deduction and any remaining carryover, subject
                  to California NOL limitation rules.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line 18 – EZ, TTA, or LAMBRA NOL Carryover Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any eligible EZ, TTA, or LAMBRA NOL carryover from
                  prior years. Complete and attach the applicable form
                  <span style={{ fontWeight: 600 }}>
                    (FTB 3805Z, FTB 3809, or FTB 3807){' '}
                  </span>
                  to calculate and support the deduction.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line 19 – Disaster loss deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Claim any eligible disaster loss carryover using Form FTB
                  3805Q. The deduction cannot reduce taxable income below zero,
                  and any unused loss must be carried forward to future years.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line 20 – Net Income for Tax Purposes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s final net income for California tax
                  purposes by combining the amounts on Lines 16 through 19 and
                  subtracting the result from Line 15.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600, fontStyle: 'initial' }}>
                    {' '}
                    Example:
                  </span>{' '}
                  The corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$150,000</span>
                  on Line 15 and total deductions of{' '}
                  <span style={{ fontWeight: 600 }}>$30,000</span> from Lines 16
                  through 19, resulting in{' '}
                  <span style={{ fontWeight: 600 }}>$120,000</span> of net
                  income for tax purposes.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Taxes (Line 21 - 30)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-taxes-l-21-30-img.webp" />
                  <source srcSet="/assets/images/100s-taxes-l-21-30-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-taxes-l-21-30-img.png"
                    loading="lazy"
                    alt="Taxes (Line 21 - 30)"
                    width={771}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 21 – Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  S corporations must compute tax at{' '}
                  <span style={{ fontWeight: 600 }}>1.5%</span>, and financial S
                  S orporations at <span style={{ fontWeight: 600 }}>3.5%</span>{' '}
                  of net income. The tax on this line cannot be less than the
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    minimum franchise tax plus any QSub annual tax
                  </span>
                  , if applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the corporation is the parent of a{' '}
                  <span style={{ fontWeight: 600 }}>
                    Qualified Subchapter S Subsidiary (QSub)
                  </span>
                  , and has paid the $800 annual tax on its behalf, add the
                  total QSub annual tax to either the net income tax or minimum
                  franchise tax (whichever applies) and enter the total on this
                  line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete <span style={{ fontWeight: 600 }}>Schedule QS</span>{' '}
                  if QSub tax applies.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Example:</span> If an S
                  corporation reports $100,000 of taxable net income, its tax is
                  $1,500 (1.5% × $100,000). If it has one California QSub, add
                  the $800 QSub annual tax, resulting in a total tax of $2,300.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Lines 22 & 23 – Tax Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name of each tax credit, the corresponding credit
                  code, and the amount claimed for each credit on Lines 22 and
                  23.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 24 – Additional Tax Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any remaining tax credits from Schedule C (100S).
                  Attach all supporting credit forms, schedules, and Schedule C
                  (100S).
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Tax credits may reduce California tax liability
                  but cannot reduce it below the minimum franchise tax and any
                  applicable QSub annual tax. Certain credit limitations and
                  carryover rules may apply.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 25 – Total Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the tax credits reported on Lines 22 through 24 to
                  determine total credits claimed. Attach Schedule C (100S).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26 – Balance
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To figure out how much tax you still owe, subtract the total
                  credits (Line 25) from the total tax (Line 21). The result
                  must be at least the minimum franchise tax plus any applicable
                  QSub annual taxes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 27 – Tax from Schedule D (100S)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Built-in gains tax. Report any built-in gains tax calculated
                  on Schedule D (100S) and attach Schedule D (100S). This tax
                  generally applies to profits realized by a corporation that
                  changed from a C corporation to a S corporation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 28 – Excess Net Passive Income Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the corporation has excess passive investment income and
                  taxable income, calculate and report the excess net passive
                  income tax. Attach a schedule showing the tax computation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 29 – Pass-Through Entity Elective Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the total pass-through entity elective tax paid during
                  the tax year. If applicable, complete and attach Form FTB 3804
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 30 – Total Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines 26 through 29 to determine the corporation’s total
                  tax liability for the tax year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Payments (line 31 – 36)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-payments-l-31-36-img.webp" />
                  <source srcSet="/assets/images/100s-payments-l-31-36-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-payments-l-31-36-img.png"
                    loading="lazy"
                    alt="Payments (line 31 – 36)"
                    width={771}
                    height={140}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 31 – Overpayment Credit from Prior Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any overpayment from the previous tax year that was
                  applied as a credit toward the current year's tax liability.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 32 – 2025 Estimated Tax/QSub Payments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report all estimated tax payments made during the tax year,
                  including any QSub annual tax payments. If applicable, attach
                  Schedule QS for QSub payments or Schedule K-1 (568) for taxes
                  paid on the corporation’s behalf by an LLC.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 33 – 2025 Withholding (Forms 592-B and/or 593)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report California withholding credits received during the tax
                  year. Attach Form 592-B and/or Form 593 if claiming the
                  credit. If withholding credits are allocated to shareholders,
                  file Form 592 with the FTB.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Do not include NCNR member’s tax reported on
                  Schedule K-1 (568), line 15e, as withholding.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 34 – Amount Paid with Extension of Time to File Tax
                  Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount paid with your tax filing extension for the
                  current tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 35 – Amounts Paid for Pass-Through Entity Elective Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total pass-through entity elective tax payments made
                  during the tax year, including electronic payments and
                  payments submitted with Form FTB 3893 or the tax return.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Pass-through entity elective tax payments must
                  be reported separately and cannot be combined with other tax
                  payments.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 36 – Total Payments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add all payments and credits reported on Lines 31 through 35
                  to determine total payments for the tax year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Refund or Amount Due (line 37 – 45)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-refund-or-amount-due-img.webp" />
                  <source srcSet="/assets/images/100s-refund-or-amount-due-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-refund-or-amount-due-img.png"
                    loading="lazy"
                    alt="Refund or Amount Due (line 37 – 45)"
                    width={771}
                    height={300}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 37 – Use tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your S corporation purchased taxable goods from an
                  out-of-state seller (such as online, mail-order, phone, or
                  in-person purchases) and California sales or use tax was not
                  paid, you may owe California use tax. Calculate the tax using
                  the Use Tax Worksheet and claim any eligible credit for tax
                  paid to another state.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Certain businesses must report use tax directly
                  to the CDTFA instead of on Form 100S. Failure to report and
                  pay use tax may result in penalties and interest.
                </Alert>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 38 – Payments Balance
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the total of payments and credits on Line 36 are more than
                  the use tax reported on Line 37, subtract Line 37 from Line 36
                  and enter the remaining balance on this line. This amount is
                  the balance due from the corporation after applying any use
                  tax due.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> in payments on
                  Line 36 and <span style={{ fontWeight: 600 }}>$145</span> of
                  use tax on Line 37, for a payments balance of{' '}
                  <span style={{ fontWeight: 600 }}>$4,855</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 39 – Use Tax Balance
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the amount on Line 37, your use tax, is more than the
                  amount on Line 36, your total payments and credits, subtract
                  Line 36 from Line 37 and enter the amount you owe here. This
                  amount is the corporation’s remaining California franchise or
                  income tax liability.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$500</span> of use tax on
                  Line 37 and <span style={{ fontWeight: 600 }}>$300</span> in
                  payments on Line 36, resulting in a use tax balance of{' '}
                  <span style={{ fontWeight: 600 }}>$200</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 40 – Franchise/Income Tax Due
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your total tax reported on Line 30 is more than your
                  payments balance on Line 38, subtract Line 38 from Line 30 and
                  enter the remaining amount due on this line. This amount
                  represents the corporation’s remaining California franchise or
                  income tax liability.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$2,150</span> on Line 30 and{' '}
                  <span style={{ fontWeight: 600 }}>$1,800</span> on Line 38, so
                  its tax due is <span style={{ fontWeight: 600 }}>$350</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 41 – Overpayment
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Line 38 (Payments) is more than Line 30 (Total Tax),
                  subtract Line 30 from Line 38 and enter the overpayment on
                  this line. The amount represents the excess payments or
                  credits available to be refunded or carried forward as a
                  credit.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> on Line 38 and{' '}
                  <span style={{ fontWeight: 600 }}>$4,200</span> on Line 30,
                  resulting in an overpayment of{' '}
                  <span style={{ fontWeight: 600 }}>$800</span>.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Attach Schedule J and any applicable supporting
                  forms.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 42 – Credit to 2026 Estimated Tax
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the part of the overpayment on Line 41 that the
                  corporation wants to use to pay its estimated tax for 2026.
                  Any excess payment remaining after that amount may be returned
                  to the corporation.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported a{' '}
                  <span style={{ fontWeight: 600 }}>$800</span> overpayment on
                  Line 41 and chose to apply{' '}
                  <span style={{ fontWeight: 600 }}>$500</span> against its
                  estimated tax for 2026.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 43 – Refund
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the corporation has an overpayment, apply any amount to
                  2026 estimated tax and enter the balance of the refund amount
                  on Line 43.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>
                    To receive the refund by direct deposit, complete:
                  </span>
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Line 43a</span> – Enter
                      the bank routing number
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Line 43b</span> – Choose
                      Account Type (Checking or Savings)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Line 43c</span> – Fill
                      in the bank account number
                    </Typography>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Verify the routing and account information with
                  the banking institution prior to filing the return to avoid
                  delays or missed refunds. Do not send a voided check or
                  deposit slip with your return.
                </Alert>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 44 – Penalties and Interest
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any penalties and interest due for the tax year. If you
                  use Exception B or Exception C on Form FTB 5806 to calculate
                  or reduce the estimated tax penalty, attach Form FTB 5806 and
                  check the box on Line 44b.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 45 – Total Amount Due
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s total amount due by adding the amounts
                  reported on Lines 39, 40, 42, and 44a, then subtracting any
                  overpayment reported on Line 41. This line represents the
                  final balance the corporation must pay with the return.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Schedule Q (Line C – U)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-q-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-q-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-q-img.png"
                    loading="lazy"
                    alt="Schedule Q (Line C – U)"
                    width={771}
                    height={420}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question C – Principal Business Activity (PBA) Code
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the six-digit California Principal Business Activity
                  (PBA) code that best describes the corporation’s primary
                  business activity and generates the largest share of
                  California receipts. Also provide the corporation’s business
                  activity and principal product or service.
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="pba-code-note"
                >
                  <b>Note :</b> Do not leave this field blank.
                </Alert>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question D – Water’s-Edge Election
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  the S corporation is using California’s water’s-edge method to
                  report income for the current tax year. Otherwise, select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question E – Qualified Subchapter S Subsidiaries (QSubs)
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  the S corporation owns one or more Qualified Subchapter S
                  Subsidiaries (QSubs). Complete and attach Schedule QS and
                  report any applicable QSub annual tax with the return.
                  Otherwise, select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question F – Date and Place of Incorporation
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s incorporation date and specify the
                  state or country where the corporation was legally
                  incorporated.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question G – Maximum Number of Shareholders
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the highest number of shareholders the S corporation had
                  at any time during the tax year.
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="maximum-shareholders-note"
                >
                  <b>Note :</b> Do not leave this field blank.
                </Alert>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question H – Business Start Date in California
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date the corporation first began doing business in
                  California or first earned income from California sources.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question I – Inactive Business Status
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  the S corporation was inactive and had no business operations
                  or income both inside and outside California during the tax
                  year. Otherwise, select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question J – IRS Audit Status
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  the S corporation is currently under audit by the IRS or was
                  audited by the IRS in a prior tax year. Otherwise, select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question K – Effective Date of Federal S Election
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the effective date on which the corporation’s federal S
                  corporation election became effective.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question L – Accounting Method
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select the accounting method used to report the corporation’s
                  income and expenses for the tax year:{' '}
                  <span style={{ fontWeight: 600 }}>Cash</span>,{' '}
                  <span style={{ fontWeight: 600 }}>Accrual</span>, or{' '}
                  <span style={{ fontWeight: 600 }}>Other</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question M – Location of Principal Accounting Records
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the address where the corporation’s main accounting
                  books and financial records are maintained.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question N – Doing Business As (DBA) Name
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your corporation’s DBA (Doing Business As) name if you
                  operate under a business name different from your legal
                  corporation's name. If you use more than one DBA, attach a
                  list of all DBA names. Leave this field blank if you do not
                  use a DBA.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question O – Information Returns Filed
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  your corporation has filed all required federal and California
                  information returns, such as Forms 1099, 8300, 592, and 592-B.
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span> if
                  any required returns have not been filed, or{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;N/A&rdquo;</span> if
                  these filing requirements do not apply to your business.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question P – Apportioning or Allocating Income
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  your S corporation uses Schedule R to apportion or allocate
                  income to California. Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span> if
                  all income is sourced entirely to California and Schedule R is
                  not required.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question Q – Reportable or Listed Transactions
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  the S corporation participated in a reportable or listed
                  transaction during the tax year and complete and attach
                  federal Form 8886 for each applicable transaction. Otherwise,
                  select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Examples of reportable transactions include
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <Typography variant="body1">
                      A tax strategy identified by the IRS or FTB as a potential
                      tax avoidance arrangement.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      A confidential tax transaction offered for a fee.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      A transaction that provides a refund or protection if the
                      expected tax benefits are disallowed.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      A transaction that generated a large tax loss exceeding
                      IRS reporting limits.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      A transaction specifically designated by the IRS as a{' '}
                      <span style={{ fontWeight: 600 }}>
                        transaction of interest
                      </span>
                      .
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      A transaction involving a tax credit of more than{' '}
                      <span style={{ fontWeight: 600 }}>$250,000</span> for an
                      asset held less than{' '}
                      <span style={{ fontWeight: 600 }}>45 days</span>.
                    </Typography>
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your corporation claimed a deduction, loss, credit, or
                  other tax benefit from any of these transactions, attach Form
                  8886 and answer{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question R – Federal Schedule M-3 Filing
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  the S corporation filed federal{' '}
                  <Link
                    href="/resources/business-tax/schedule-m3-form-1120s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule M-3
                  </Link>{' '}
                  (Form 1120-S) with its federal tax return. Otherwise, select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question S – Form FTB 3544 Attached
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  Form FTB 3544, Side 2, Part B (List of Assigned Credit
                  Received and/or Claimed by Assignee) is attached to the
                  return. Otherwise, select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question T – Aggregated or Grouped Activities
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the appropriate box if the corporation combined multiple
                  business activities for tax reporting purposes.
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>
                        At-Risk Activities (IRC Section 465):
                      </span>{' '}
                      Select if activities were aggregated to calculate at-risk
                      limitations.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>
                        Passive Activities (IRC Section 469):
                      </span>{' '}
                      Select if activities were grouped to determine passive
                      income, losses, or credits.
                    </Typography>
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Leave both boxes unchecked if neither applies to the
                  corporation.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Question U – Unclaimed Property Reporting
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  the corporation has previously filed an Unclaimed Property
                  Holder Remit Report with the California State Controller’s
                  Office. If yes, enter the date the last report was filed and
                  the amount remitted.
                </Typography>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 2 }}
                >
                  <b>Important:</b> Enter the remitted amount with{' '}
                  <span style={{ fontWeight: 600 }}>
                    dollars and cents exactly as paid
                  </span>
                  —do not round cents to the nearest dollar.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Schedule J Additional Taxes and Recapture of Tax Credits.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use Schedule J to report additional taxes, interest, and
                  recapture of credits that could affect the corporation’s total
                  tax liability. Enter applicable add-on taxes, interest
                  amounts, and credit recapture details, along with any required
                  supporting schedules or forms, in the lines below.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-j-add-on-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-j-add-on-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-j-add-on-img.png"
                    loading="lazy"
                    alt="Schedule J Additional Taxes and Recapture of Tax Credits."
                    width={771}
                    height={200}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1 – LIFO Recapture Tax
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the current year installment of LIFO recapture tax due
                  when a C corporation elected S corporation status and
                  previously used the LIFO inventory method. This tax is a
                  gradual repayment of the deferred LIFO benefits.
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="lifo-recapture-note"
                >
                  <b>Note :</b> Report only the installment amount for the tax
                  year.
                </Alert>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2 – Long-Term Contracts (Look-Back Interest)
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report interest calculated under the look-back method for
                  completed long-term contracts. If applicable, complete and
                  attach Form FTB 3834. Enter the interest paid or refunded for
                  the tax year. Use a negative amount if the result is a refund
                  or credit.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3a – Installment Sales Interest (Timeshares & Residential
                  Lots)
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report interest on tax for installment sales of certain
                  timeshares and residential lots under IRC Section 453(l)(3),
                  if applicable. Use rates from FTB Pub. 1138 and attach a
                  computation schedule. Add the interest accrued for the year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3b – Installment Sales Interest (Nondealer Installment
                  Obligations)
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report interest on tax related to installment obligations from
                  certain property sales under IRC Section 453A(c), if the
                  obligation is still outstanding at the end of the tax year.
                  Use rates from FTB Pub. 1138 and attach a computation
                  schedule. Include the interest due for the year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4 – IRC Section 197 Election
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report tax if the corporation elects to recognize gain on the
                  sale of an intangible asset under IRC Section 197(f)(9)(B)(ii)
                  (related person exception to anti-churning rules). Enter the
                  amount of tax for the tax year in the appropriate tax amount
                  field.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5 – Credit Recapture
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report any California tax credits claimed in prior years that
                  must be repaid under the recapture rules. Includes credits
                  such as FTB 3835, FTB 3531, FTB 3554, Environmental Tax
                  Credit, and Farmworker Housing Credit. Enter the total
                  recaptured amount for the year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6 – Total Adjustment from Schedule J
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Combine the amounts from Lines 1 through 5 and determine the
                  net adjustment. Enter the resulting amount on Form 100S, Side
                  2, Line 40 or Line 41, as applicable, and write{' '}
                  <span style={{ fontWeight: 600 }}>
                    &ldquo;Schedule J&rdquo;
                  </span>{' '}
                  next to the adjusted line.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Signatures & Paid Preparer Use Only
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-signature-img.webp" />
                  <source srcSet="/assets/images/100s-signature-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-signature-img.png"
                    loading="lazy"
                    alt="Signatures & Paid Preparer Use Only"
                    width={771}
                    height={230}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The Signature Section is there to verify that the tax return
                  is correct and that it has been officially approved to be
                  filed.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Signature of Officer:</span>{' '}
                  The return must be signed by an authorized officer of the
                  corporation who must certify under penalty of perjury that all
                  of the information supplied is true, correct, and complete.
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Title:</span> Enter the
                      official designation of the signing officer (e.g.,
                      President, Vice President, CFO).
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Date:</span> Enter the
                      date on which the return is signed.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Telephone:</span>{' '}
                      Provide a valid contact number for the signing officer.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>
                        Officer Email (Optional):
                      </span>{' '}
                      Can be provided to allow for communication.
                    </Typography>
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Paid Preparer’s Use Only
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section is completed if a paid tax preparer prepares the
                  return.
                </Typography>

                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>
                        Preparer’s Name &amp; Signature:
                      </span>{' '}
                      Enter the name and signature of the tax preparer
                      responsible for preparing the return.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Date:</span> Enter the
                      date the return was signed by the preparer.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>
                        Self-Employed Checkbox:
                      </span>{' '}
                      Check if the preparer is self-employed.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>PTIN:</span> Furnish the
                      Preparer Tax Identification Number (PTIN).
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>
                        Firm’s Name and Address:
                      </span>{' '}
                      Enter the tax preparation firm’s name and complete
                      address.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Firm’s FEIN:</span>{' '}
                      Enter the firm’s Employer Identification Number.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Telephone:</span>{' '}
                      Provide the firm’s contact number.
                    </Typography>
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  FTB Discussion Authorization
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> if
                  you want the Franchise Tax Board (FTB) to discuss your Form
                  100S with the paid preparer who signed the return. This
                  authorization allows the preparer to provide missing
                  information, discuss the return’s processing status, refund,
                  or payment details, and respond to certain FTB notices related
                  to errors, offsets, or return preparation.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span> if
                  you do not want the FTB to communicate with the paid preparer
                  regarding your return.
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="ftb-discussion-authorization-note"
                >
                  <b>Note :</b> The discussion authorization applies only to the
                  individual paid preparer who signed the return. It does not
                  allow the preparer to receive refunds, act for the
                  corporation, or represent the corporation before the FTB.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Schedule F - Computation of Trade or Business Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use Schedule F to determine the corporation’s business profit
                  (or loss) for the tax year. Enter the corporation’s sales,
                  income, cost of goods sold, and allowable business expenses in
                  the next few lines to determine the corporation’s ordinary
                  business income or loss for the year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Income (Line 1 – 6)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-f-income-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-f-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-f-income-img.png"
                    loading="lazy"
                    alt="Schedule F - Computation of Trade or Business Income
"
                    width={771}
                    height={130}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1a – Gross Receipts or Sales
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the gross income your business made from the sale of
                  goods or the provision of services for the tax year before
                  taking off any returns, refunds, discounts, or cost of goods
                  sold.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If your
                  business had <span style={{ fontWeight: 600 }}>$100,000</span>{' '}
                  in sales during the year, enter{' '}
                  <span style={{ fontWeight: 600 }}>$100,000</span> on Line 1a
                  even if some of the products were returned or refunded later.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1b – Returns and Allowances
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of customer refunds, returned
                  merchandise, rebates, or price adjustments for the year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If you had
                  customers return products that cost{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span>, enter{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> on Line 1b.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1c – Balance
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Take Line 1a minus Line 1b to arrive at your Net Sales.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Formula:</span> Gross
                  Receipts or Sales (Line 1a) – Returns and Allowances (Line 1b)
                  = Balance (Line 1c).
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$100,000</span> –{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$95,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2 – Cost of Goods Sold (COGS)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount from{' '}
                  <span style={{ fontWeight: 600 }}>Schedule V, Line 8</span>,
                  which is the direct costs of producing or buying the goods
                  sold during the tax year. These costs may include inventory,
                  raw materials, manufacturing expenses, and direct labour.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If Schedule
                  V, Line 8 is <span style={{ fontWeight: 600 }}>$40,000</span>,
                  enter <span style={{ fontWeight: 600 }}>$40,000</span> on Line
                  2.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3 – Gross Profit
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 2 (Cost of Goods Sold) from Line 1c (Balance)
                  and you will have your gross profit from business operations.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$95,000</span> –{' '}
                  <span style={{ fontWeight: 600 }}>$40,000</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$55,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4 – Net Gain (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total net gain or loss from the sale, exchange, or
                  disposition of business assets during the tax year. Attach a
                  supporting schedule showing the details of each transaction
                  and how the gain or loss was calculated.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If your
                  corporation sold business equipment and made a{' '}
                  <span style={{ fontWeight: 600 }}>$5,000 gain</span>, enter{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> on Line 4. If
                  the sale resulted in a loss of{' '}
                  <span style={{ fontWeight: 600 }}>$2,000</span>, enter{' '}
                  <span style={{ fontWeight: 600 }}>($2,000)</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5 – Other Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any business income or loss not shown on Lines 1 to 4.
                  Attach a supporting schedule for each item and the amount
                  reported.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If the
                  corporation had{' '}
                  <span style={{ fontWeight: 600 }}>$3,000</span> of
                  miscellaneous business income that did not belong on another
                  line, enter <span style={{ fontWeight: 600 }}>$3,000</span> on
                  Line 5. If it was a loss of{' '}
                  <span style={{ fontWeight: 600 }}>$1,000</span>, enter{' '}
                  <span style={{ fontWeight: 600 }}>($1,000)</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6 – Total Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add <span style={{ fontWeight: 600 }}>Lines 3, 4, and 5</span>{' '}
                  to calculate the corporation’s total income or loss from trade
                  or business activities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$55,000</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$3,000</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$63,000</span>. If any line
                  is negative, subtract that amount when calculating the total.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Deductions (Line 7 – 22)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-deductions-img.webp" />
                  <source srcSet="/assets/images/100s-deductions-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-deductions-img.png"
                    loading="lazy"
                    alt="Deductions (Line 7 – 22)"
                    width={771}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7 – Compensation of Officers
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total compensation paid to corporate officers during
                  the tax year. If the corporation’s total receipts are{' '}
                  <span style={{ fontWeight: 600 }}>$150,000 or more</span>,
                  attach a schedule listing each officer’s name, SSN, percentage
                  of time devoted to the business, percentage of stock
                  ownership, compensation paid, and the calculation of the
                  deductible officer compensation reported on the return.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If officers
                  received a total of{' '}
                  <span style={{ fontWeight: 600 }}>$75,000</span> in
                  compensation, enter{' '}
                  <span style={{ fontWeight: 600 }}>$75,000</span> on Line 7 and
                  attach the required supporting schedule.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 8 – Salaries and Wages
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total salaries and wages paid to employees during
                  the tax year, excluding compensation reported on{' '}
                  <span style={{ fontWeight: 600 }}>
                    Line 7 (Compensation of Officers)
                  </span>
                  . Include amounts paid for employee services that are
                  deductible as business expenses.
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="cqso-compensation-note"
                >
                  <b>Note :</b> California Qualified Stock Option (CQSO)
                  compensation that is excluded from an employee’s gross income
                  is generally{' '}
                  <span style={{ fontWeight: 600 }}>not deductible</span> by the
                  S corporation.
                </Alert>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 9 – Repairs and Maintenance
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the cost of ordinary repairs and maintenance incurred
                  during the tax year to keep business property, equipment, and
                  facilities in efficient operating condition.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 10 – Bad Debts
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter debts that became uncollectible during the tax year and
                  were previously included in the corporation’s income. Do not
                  include estimated bad debts or amounts not previously reported
                  as income.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 11 – Rents
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter rent paid or incurred for business property, equipment,
                  vehicles, or other assets used in the corporation’s
                  operations.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation paid{' '}
                  <span style={{ fontWeight: 600 }}>$24,000</span> for rent of
                  offices during the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12 – Taxes
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter taxes paid or incurred during the tax year that are
                  directly related to the corporation’s business operations. Do
                  not include federal income taxes.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation paid{' '}
                  <span style={{ fontWeight: 600 }}>$3,500</span> in state
                  payroll taxes and local business taxes during the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 13 – Interest
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of interest you paid or incurred during the
                  tax year on debts related to your business, such as loans,
                  mortgages, or lines of credit used in operating your
                  corporation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 14a – Depreciation
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total depreciation expense claimed for business
                  assets during the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> For
                  instance, the corporation took{' '}
                  <span style={{ fontWeight: 600 }}>$12,000</span> depreciation
                  on its business equipment and vehicles.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 14b – Less Depreciation Reported Elsewhere
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any depreciation already deducted elsewhere on the
                  return, such as amounts included in the cost of goods sold or
                  other schedules.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$2,000</span> of
                  depreciation included in cost of goods sold calculation.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 14c – Balance
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 14b from Line 14a to calculate the depreciation
                  deduction reported on Schedule F. The amount calculated is the
                  net allowable depreciation deduction for this schedule.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$12,000</span> –{' '}
                  <span style={{ fontWeight: 600 }}>$2,000</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$10,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15 – Depletion
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the depletion deduction for natural resources, such as
                  oil, gas, minerals, timber, or other qualifying properties,
                  claimed during the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation claimed a{' '}
                  <span style={{ fontWeight: 600 }}>$3,500</span> depletion
                  deduction related to mineral extraction activities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16 – Advertising
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter advertising and promotional expenses paid or incurred
                  during the tax year to market the corporation’s products,
                  services, or business activities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 17 – Pension, Profit-Sharing Plans, etc.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter contributions made during the tax year to employee
                  pension, profit-sharing, stock bonus, or other qualified
                  retirement plans.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18 – Employee Benefit Programs
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost of employee benefit programs paid or incurred
                  during the tax year, such as health insurance, life insurance,
                  education assistance, and other employee welfare benefits. Do
                  not include amounts reported elsewhere on the return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 19a – Total Travel and Entertainment
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount paid or incurred during the tax year
                  for business-related travel, meals, and entertainment
                  expenses.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation has{' '}
                  <span style={{ fontWeight: 600 }}>$6,000</span> in business
                  travel, meals, and entertainment costs during the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 19b – Deductible Amount
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the deductible portion of the travel and entertainment
                  expenses allowed under applicable tax laws.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If{' '}
                  <span style={{ fontWeight: 600 }}>$4,500</span> of the{' '}
                  <span style={{ fontWeight: 600 }}>$6,000</span> in travel and
                  entertainment expenses is deductible, enter{' '}
                  <span style={{ fontWeight: 600 }}>$4,500</span> on Line 19b.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 20 – Other Deductions
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter deductible business expenses not reported on Lines 7
                  through 19. Attach a supporting schedule listing each
                  deduction and its amount. Do{' '}
                  <span style={{ fontWeight: 600 }}>not</span> include the
                  dividend deduction on this line; report dividend deductions on{' '}
                  <span style={{ fontWeight: 600 }}>
                    Form 100S, Side 2, Line 9 or Line 10
                  </span>
                  , as applicable.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation incurred{' '}
                  <span style={{ fontWeight: 600 }}>$2,500</span> in bank fees,
                  professional dues, and other miscellaneous business expenses
                  not reported elsewhere.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 21 – Total Deductions
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>Lines 7 through 20</span> to
                  calculate the corporation’s total deductible business expenses
                  for the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 22 – Ordinary Income (Loss) From Trade or Business
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract{' '}
                  <span style={{ fontWeight: 600 }}>
                    Line 21 (Total Deductions)
                  </span>{' '}
                  from{' '}
                  <span style={{ fontWeight: 600 }}>
                    Line 6 (Total Income or Loss)
                  </span>{' '}
                  to calculate the corporation’s ordinary income or loss from
                  trade or business activities. Enter the result on{' '}
                  <span style={{ fontWeight: 600 }}>Line 22</span> and on{' '}
                  <span style={{ fontWeight: 600 }}>
                    Form 100S, Page 1, Line 1
                  </span>
                  .
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If Line 6 is{' '}
                  <span style={{ fontWeight: 600 }}>$313,000</span> and Line 21
                  is <span style={{ fontWeight: 600 }}>$286,500</span>, ordinary
                  income is <span style={{ fontWeight: 600 }}>$26,500</span> (
                  <span style={{ fontWeight: 600 }}>$313,000 - $286,500</span>).
                  If your total deductions are greater than your total income,
                  enter the loss.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Schedule L – Balance Sheet
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule L shows the corporation’s financial position for the
                  tax year. In the following lines, report the corporation’s
                  assets, liabilities, and shareholders’ equity at the beginning
                  and end of the year using the business records. Corporations
                  with total receipts and total assets below{' '}
                  <span style={{ fontWeight: 600 }}>$250,000</span> may not be
                  required to complete this schedule.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Assets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-f-assets-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-f-assets-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-f-assets-img.png"
                    loading="lazy"
                    alt="Schedule L – Balance Sheet"
                    width={771}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1 – Cash
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount of cash the corporation had on hand and
                  in bank accounts at the beginning and end of the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2a – Trade Notes and Accounts Receivable
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount owed to the corporation by customers
                  for goods sold or services provided that remained unpaid as of
                  the balance sheet date.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had{' '}
                  <span style={{ fontWeight: 600 }}>$25,000</span> in
                  outstanding customer invoices at year-end.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2b – Less Allowance for Bad Debts
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the estimated amount of accounts receivable that may not
                  be collectible.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation estimated{' '}
                  <span style={{ fontWeight: 600 }}>$1,000</span> of outstanding
                  receivables as uncollectible.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3 – Inventories
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of inventory held for sale in the ordinary
                  course of business as of the beginning and end of the tax
                  year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had{' '}
                  <span style={{ fontWeight: 600 }}>$40,000</span> of inventory
                  on hand at year-end.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4 – Federal and State Government Obligations
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of federal or state government securities and
                  obligations owned by the corporation at the beginning and end
                  of the tax year, such as U.S. Treasury securities and
                  municipal bonds.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5 – Other Current Assets
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the value of current assets not reported on Lines 1
                  through 4 that are expected to be converted to cash, sold, or
                  used within one year. Attach a supporting schedule listing
                  each asset and its value.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6 – Loans to Shareholders
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount of money the corporation owes its
                  shareholders. Please attach a loan schedule with loan
                  information and outstanding loan balances.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7 – Mortgage and Real Estate Loans
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount owed to the corporation from mortgage
                  loans and other loans secured by real estate.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 8 – Other Investments
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of investments not reported elsewhere on
                  Schedule L. Attach a schedule listing each investment and its
                  value.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation held{' '}
                  <span style={{ fontWeight: 600 }}>$20,000</span> in corporate
                  stock and mutual fund investments.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 9a – Buildings and Other Fixed Depreciable Assets
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost or basis of buildings, machinery, equipment,
                  vehicles, and other depreciable business assets owned by the
                  corporation.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation owned buildings and equipment with a total cost of{' '}
                  <span style={{ fontWeight: 600 }}>$150,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 9b – Less Accumulated Depreciation
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total depreciation claimed on the assets reported on
                  Line 9a through the balance sheet date.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had accumulated depreciation of{' '}
                  <span style={{ fontWeight: 600 }}>$40,000</span> on its
                  buildings and equipment.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 10a – Depletable Assets
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost or basis of natural resource properties owned
                  by the corporation, such as oil, gas, mineral, or timber
                  properties.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation owned mineral properties with a cost basis of{' '}
                  <span style={{ fontWeight: 600 }}>$100,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 10b – Less Accumulated Depletion
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of total depletion claimed on the depletable
                  assets reported on Line 10a.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation experienced a{' '}
                  <span style={{ fontWeight: 600 }}>$25,000</span> depletion of
                  its mineral properties.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 11 – Land (Net of Any Amortization)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of land owned by the corporation, less by any
                  amortization claimed, if applicable.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> At the end
                  of the year the corporation owned land with a book value of{' '}
                  <span style={{ fontWeight: 600 }}>$75,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12a – Intangible Assets (Amortizable Only)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost or basis of amortizable intangible assets owned
                  by the corporation, such as copyrights, trademarks,
                  franchises, or goodwill.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had amortizable intangible assets that had a book
                  value of <span style={{ fontWeight: 600 }}>$30,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12b – Less Accumulated Amortization
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amortization claimed on the intangible assets
                  reported on Line 12a.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had claimed{' '}
                  <span style={{ fontWeight: 600 }}>$8,000</span> in accumulated
                  amortization on its intangible assets.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 13 – Other Assets
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of assets not shown on Lines 1 through 12.
                  List all assets and their value. Include a supporting
                  schedule.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 14 – Total Assets
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 1 through 13 to calculate the corporation’s total
                  assets.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Liabilities and shareholders’ equity
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-liabilities-img.webp" />
                  <source srcSet="/assets/images/100s-liabilities-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-liabilities-img.png"
                    loading="lazy"
                    alt="Liabilities and shareholders’ equity
"
                    width={771}
                    height={230}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report amounts for liabilities and equity similarly at the
                  beginning and end of the tax year:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15 – Accounts Payable
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount the corporation owes to vendors,
                  suppliers, and other creditors for business expenses or
                  purchases that remain unpaid.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16 – Mortgages, Notes, Bonds Payable in Less Than 1 Year
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount of mortgages, notes, bonds, and other debts
                  that are due within one year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 17 – Other Current Liabilities
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter current liabilities not reported on Lines 15 or 16 that
                  are due within one year. Attach a schedule listing each
                  liability and its amount.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18 – Loans From Shareholders
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount of money borrowed from the shareholders
                  by the corporation. Please attach a schedule of loan details
                  and balance outstanding.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 19 – Mortgages, Notes, Bonds Payable in 1 Year or More
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of mortgages, notes, bonds, and other debts
                  payable in more than one year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had{' '}
                  <span style={{ fontWeight: 600 }}>$80,000</span> in long-term
                  business loans payable after 1 year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 20 – Other Liabilities
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter liabilities that are not reported on Lines 15 through
                  19. Attach a list of each liability and the amount of each
                  liability.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 21 – Capital Stock
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total value of stock issued by the corporation to
                  its shareholders.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 22 – Paid-In or Capital Surplus
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any other amounts contributed to the corporation by
                  shareholders that are not included in capital stock.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> During the
                  tax year, the corporation received an additional{' '}
                  <span style={{ fontWeight: 600 }}>$20,000</span> from
                  shareholders.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 23 – Retained Earnings
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s accumulated earnings or losses
                  retained in the business after distributions to shareholders.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had{' '}
                  <span style={{ fontWeight: 600 }}>$75,000</span> retained
                  earnings at the end of the year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 24 – Adjustments
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any adjustments to shareholders’ equity not reported on
                  Lines 21 through 23. Attach a schedule listing each adjustment
                  and its amount.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$3,000</span> in prior-year
                  accounting adjustments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 25 – Less Cost of Treasury Stock
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost of the corporation’s own stock that was
                  repurchased and held by the corporation as treasury stock.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation held of treasury stock{' '}
                  <span style={{ fontWeight: 600 }}>at year-end</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26 – Total Liabilities and Shareholders’ Equity
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 15 through 25 to calculate the corporation’s total
                  liabilities and shareholders’ equity.
                </Typography>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 2 }}
                >
                  <b>Tip:</b> Even if Schedule L is not required, maintain
                  accurate balance sheet records for your tax and financial
                  reporting files in case the FTB requests it later
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Schedule M-1 – Reconciliation of Income (Loss) per Books with
                  Income (Loss) per Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule M-1 is used to explain the differences between the
                  corporation’s book income and taxable income reported on the
                  tax return. In the following lines, report adjustments for
                  income and expenses that are treated differently for
                  accounting and tax purposes. Corporations with total receipts
                  and total assets below{' '}
                  <span style={{ fontWeight: 600 }}>$250,000</span> may not be
                  required to complete this schedule. If{' '}
                  <span style={{ fontWeight: 600 }}>federal Schedule M-3</span>{' '}
                  was filed, attach a copy with the return.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-m1-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-m1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-m1-img.png"
                    loading="lazy"
                    alt="Schedule M-1 – Reconciliation of Income (Loss) per Books with Income (Loss) per Return	"
                    width={771}
                    height={230}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1 – Net Income per Books
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s net income or loss from its financial
                  records before making any tax adjustments.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$120,000</span> of net
                  income in its accounting records.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2 – Income Included on Schedule K, Lines 1 Through 10b,
                  Not Recorded on Books This Year
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter income reported on Schedule K, Lines 1 through 10b, that
                  was not included in the corporation’s book income for the
                  current year. Attach a schedule itemizing each amount.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> Tax-exempt
                  interest income of{' '}
                  <span style={{ fontWeight: 600 }}>$2,000</span> reported on
                  Schedule K but not recorded in the corporation’s books.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3 – Expenses Recorded on Books This Year Not Included on
                  Schedule K
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter expenses recorded in the corporation’s books that are
                  not deducted on Schedule K, Lines 1 through 12f. Attach a
                  schedule itemizing each amount.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3a – Depreciation
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter depreciation recorded on the books but not deducted on
                  Schedule K.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3b – State Taxes
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter state taxes recorded in the books that are not
                  deductible on Schedule K.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3c – Travel and Entertainment
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter non-deductible travel and entertainment expenses
                  recorded in the books.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3d – Other
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter other expenses recorded on the books but not included on
                  Schedule K. Attach details for each item.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3e – Total
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 3a through 3d to calculate total book expenses not
                  included on Schedule K.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$2,000</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$1,500</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$500</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$9,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4 – Total
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>
                    Line 1 through Line 3e
                  </span>{' '}
                  to calculate the total book income and adjustments.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$120,000</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$2,000</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$9,000</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$131,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5 – Income Recorded on Books This Year Not Included on
                  Schedule K
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter income recorded in the corporation’s books that is not
                  included on Schedule K, Lines 1 through 10b.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5a – Tax-Exempt Interest
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter tax-exempt interest income recorded on the books.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> Tax-exempt
                  municipal bond interest of{' '}
                  <span style={{ fontWeight: 600 }}>$1,500</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5b – Other
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter other book income not included on Schedule K. Attach
                  details for each item.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation recorded{' '}
                  <span style={{ fontWeight: 600 }}>$500</span> of other
                  non-taxable income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5c – Total
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 5a and 5b.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$1,500</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$500</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$2,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6 – Deductions Included on Schedule K Not Charged Against
                  Book Income This Year
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter deductions reported on Schedule K, Lines 1 through 12f,
                  that were not recorded as expenses in the corporation’s books.
                  Attach details for each item.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6a – Itemized Deductions
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter deductions included on Schedule K but not charged
                  against book income.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6b – State Tax Refunds
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter state tax refunds included on Schedule K but not
                  recorded in book income.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported a{' '}
                  <span style={{ fontWeight: 600 }}>$1,200</span> state tax
                  refund.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6c – Other
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter other deductions included on Schedule K but not recorded
                  in the books.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6d – Total
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 6a through 6c.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$3,000</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$1,200</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$800</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7 – Total
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add <span style={{ fontWeight: 600 }}>Line 5c</span> and{' '}
                  <span style={{ fontWeight: 600 }}>Line 6d</span> to calculate
                  the total income and deductions not reflected in book income.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$2,000</span> +{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$7,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 8 – Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract <span style={{ fontWeight: 600 }}>Line 7</span> from{' '}
                  <span style={{ fontWeight: 600 }}>Line 4</span> to calculate
                  the corporation’s income or loss per the California return.
                  Enter the result on{' '}
                  <span style={{ fontWeight: 600 }}>
                    Schedule K, Line 19, Column (d)
                  </span>
                  .
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$131,000</span> –{' '}
                  <span style={{ fontWeight: 600 }}>$7,000</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$124,000</span>.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Schedule M-2 – CA Accumulated Adjustments Account (AAA), Other
                  Adjustments Account (OAA), and Other Retained Earnings
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule M-2 is used to track changes in the corporation’s
                  accumulated adjustments account, retained earnings, and
                  distributions to shareholders during the tax year. Report
                  additions, reductions and distributions relating to the
                  Accumulated Adjustments Account (AAA), Other Adjustments
                  Account (OAA) and retained earnings balances in the following
                  lines. Attach any statements or schedules for distributions,
                  elections, or other adjustments reported on this schedule.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-m2-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-m2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-m2-img.png"
                    loading="lazy"
                    alt="Schedule M-2 – CA Accumulated Adjustments Account (AAA), Other Adjustments Account (OAA), and Other Retained Earnings
"
                    width={771}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1 – Balance at Beginning of Year
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the beginning balances for the Accumulated Adjustments
                  Account (AAA), Other Adjustments Account (OAA), and Other
                  Retained Earnings at the start of the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2 – Ordinary Income (Form 100S, Side 1, Line 1)
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the ordinary income or loss from Form 100S, Side 1, Line
                  1 to the Accumulated Adjustments Account (AAA).
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3 – Other Additions
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any additional increases not included on Line 2 and
                  credit to the Accumulated Adjustments Account (AAA), Other
                  Adjustments Account (OAA), or Other Retained Earnings.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4 – Loss from Form 100S, Side 1, Line 1
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the loss from Form 100S, Side 1, Line 1 and subtract it
                  from the Accumulated Adjustments Account (AAA).
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5 – Other Reductions
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any reductions that are not on Line 4 and distribute to
                  the Accumulated Adjustments Account (AAA), Other Adjustments
                  Account (OAA), or Other Retained Earnings.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6 – Combine Line 1 Through Line 5
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total combined balances of all accounts shown on
                  Lines 1 through 5 of Schedule M-2.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> Following
                  the merger of Lines 1 through 5, the company reports an ending
                  AAA balance of{' '}
                  <span style={{ fontWeight: 600 }}>$75,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7 – Distributions Other than Dividend Distributions
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total distributions to shareholders for the tax
                  year. Do not include dividend distributions paid out of
                  accumulated earnings and profits (E&amp;P).
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation paid{' '}
                  <span style={{ fontWeight: 600 }}>$15,000</span> in
                  non-dividend cash distributions to shareholders.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 8 – Balance at End of Year
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the ending balance of each account after the
                  distributions reported on Line 7 are subtracted from the
                  totals on Line 6: Accumulated Adjustments Account (AAA), Other
                  Adjustments Account (OAA), and Other Retained Earnings.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had an AAA balance of{' '}
                  <span style={{ fontWeight: 600 }}>$75,000</span> on Line 6 and
                  paid out <span style={{ fontWeight: 600 }}>$15,000</span> to
                  shareholders on Line 7, ending with an AAA balance of{' '}
                  <span style={{ fontWeight: 600 }}>$60,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 9 – Retained Earnings at End of Year
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the ending balances from Line 8, columns (a) through (c)
                  to obtain the corporation’s total ending retained earnings.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reports ending balances of{' '}
                  <span style={{ fontWeight: 600 }}>$60,000</span> in AAA,{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> in OAA and{' '}
                  <span style={{ fontWeight: 600 }}>$10,000</span> in Other
                  Retained Earnings, for a total of{' '}
                  <span style={{ fontWeight: 600 }}>$75,000</span> of retained
                  earnings.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 10 – C Corporation’s Earnings and Profits (E&amp;P)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the remaining balance of accumulated earnings and
                  profits (E&amp;P) from years when the corporation was taxed as
                  a C corporation. Complete this line only if the S corporation
                  previously operated as a C corporation and still has
                  undistributed C corporation E&amp;P at the end of the tax
                  year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If the
                  corporation has{' '}
                  <span style={{ fontWeight: 600 }}>$25,000</span> of
                  undistributed C corporation E&amp;P from prior years, enter{' '}
                  <span style={{ fontWeight: 600 }}>$25,000</span> on Line 10.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Schedule V – Cost of Goods Sold
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule V is used to calculate the corporation’s cost of
                  goods sold (COGS) for the tax year. In the following lines,
                  enter inventory costs, purchases, labour costs, and other
                  direct production expenses related to goods sold during the
                  year. The total cost of goods sold calculated in this schedule
                  is carried to Schedule F, Line 2.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-v-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-v-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-v-img.png"
                    loading="lazy"
                    alt="Schedule V – Cost of Goods Sold
"
                    width={771}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1 – Inventory at Beginning of Year
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the value of inventory the corporation had on hand at
                  the beginning of the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2 – Purchases
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost of inventory, raw materials, or goods purchased
                  during the tax year for resale or production.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation purchased{' '}
                  <span style={{ fontWeight: 600 }}>$60,000</span> of inventory
                  during the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3 – Cost of Labor
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s direct labour costs related to the
                  production of goods or the provision of production services
                  during the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had direct labour costs of{' '}
                  <span style={{ fontWeight: 600 }}>$25,000</span> in the year
                  of account.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4 – Other IRC Section 263A Costs
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s additional costs required to be
                  capitalized under IRC Section 263A. Attach a schedule listing
                  each cost and amount.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$8,000</span> in indirect
                  production and storage costs under IRC Section 263A.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5 – Other Costs
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s other inventory or production costs
                  not reported on Lines 1 through 4. Attach a schedule listing
                  each cost and amount.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$4,000</span> in packaging
                  and freight costs.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6 – Total
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 1 through 5 to calculate the corporation’s total
                  cost of goods available during the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7 – Inventory at End of Year
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of the corporation’s inventory remaining on
                  hand at the end of the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had ending inventory valued at{' '}
                  <span style={{ fontWeight: 600 }}>$30,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 8 – Cost of Goods Sold
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 7 from Line 6 to calculate the corporation’s
                  cost of goods sold for the tax year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If Line 6 is{' '}
                  <span style={{ fontWeight: 600 }}>$132,000</span> and Line 7
                  is <span style={{ fontWeight: 600 }}>$30,000</span>, the cost
                  of goods sold is{' '}
                  <span style={{ fontWeight: 600 }}>$102,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Inventory Method Change Question
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether the corporation changed its method of
                  determining inventory quantities, costs, or valuations between
                  the beginning and end of the tax year.
                </Typography>

                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                  <li>
                    <Typography variant="body1">
                      Select{' '}
                      <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span>{' '}
                      if the inventory method changed during the year.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Select{' '}
                      <span style={{ fontWeight: 600 }}>&ldquo;No&rdquo;</span>{' '}
                      if the same inventory method was used throughout the year.
                    </Typography>
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Additional Inventory Details
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the corporation changed its inventory method during the tax
                  year, select{' '}
                  <span style={{ fontWeight: 600 }}>&ldquo;Yes&rdquo;</span> and
                  attach an explanation of the change. Also, enter the
                  corporation’s California seller’s permit number, if
                  applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Method of Inventory Methods
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Specify the inventory valuation method used by the
                  corporation, such as FIFO, LIFO, or Lower of Cost or Market.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  LIFO Inventory Method Adoption
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box if the corporation adopted the LIFO (Last-In,
                  First-Out) inventory method for any goods during the tax year.
                  If checked, attach federal Form 970.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Closing Inventory Under LIFO
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the corporation used the LIFO inventory method during the
                  tax year, enter the amount of closing inventory calculated
                  under LIFO.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Schedule K – Shareholder’s Shares of Income, Deductions,
                  Credits, and Other Tax Items
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule K is used to summarize the corporation’s income,
                  deductions, credits, and other tax items that are passed
                  through to shareholders. In the following lines, report
                  ordinary business income, rental income, capital gains,
                  deductions, credits, and other separately stated items
                  allocated to shareholders for the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Income (Loss)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-k-income-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-k-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-k-income-img.png"
                    loading="lazy"
                    alt="Schedule K – Shareholder’s Shares of Income, Deductions, Credits, and Other Tax Items"
                    width={771}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1 – Ordinary Business Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s ordinary business income or loss.
                  Include in column (c) any California adjustments to ordinary
                  income that are not separately stated, such as the addback of
                  the California minimum franchise tax or the
                  <span style={{ fontWeight: 600 }}>1.5%</span> tax deducted for
                  federal purposes.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation had a{' '}
                  <span style={{ fontWeight: 600 }}>$2,500</span> California
                  adjustment for addback of franchise tax.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2 – Net Rental Real Estate Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the total net income or loss from all rental real
                  estate activities of the S corporation, including all related
                  rental income and expenses. If there is more than one rental
                  property, attach a separate schedule showing the income or
                  loss for each property along with required passive activity
                  details, and attach Form FTB 3801 to Form 100S.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Examples:</span>{' '}
                  Maintenance, repairs, taxes, depreciation, insurance,
                  utilities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3a – Other Gross Rental Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s gross income or loss from rental
                  activities not reported on Line 2.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported other gross rental income of{' '}
                  <span style={{ fontWeight: 600 }}>$15,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3b – Expenses From Other Rental Activities
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter expenses for other rental activities. Attach a schedule
                  that lists each expense.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation incurred{' '}
                  <span style={{ fontWeight: 600 }}>$6,000</span> of expenses in
                  connection with rental activity.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3c – Other Net Rental Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 3b from Line 3a to determine the corporation’s
                  net rental income or loss from other rental activities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span>{' '}
                  <span style={{ fontWeight: 600 }}>$15,000</span> –{' '}
                  <span style={{ fontWeight: 600 }}>$6,000</span> ={' '}
                  <span style={{ fontWeight: 600 }}>$9,000</span> net rental
                  income.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4 – Interest Income
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the corporation’s taxable interest income that qualifies
                  as portfolio income, such as interest earned from bank
                  accounts, loans, or investments.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5 – Dividends
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the corporation’s taxable dividend income that qualifies
                  as portfolio income, including dividend distributions received
                  from domestic or foreign corporations and other qualifying
                  investments.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6 – Royalties
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the corporation’s royalty income that qualifies as
                  portfolio income, including royalties received from
                  copyrights, patents, trademarks, franchises, mineral
                  properties, and other intangible rights.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7 – Net Short-Term Capital Gain (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s net short-term capital gain or loss
                  from the sale, exchange, or disposition of capital assets held
                  for one year or less. Attach Schedule D (100S).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 8 – Net Long-Term Capital Gain (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s net long-term capital gain or loss
                  from the sale, exchange, or disposition of capital assets held
                  for more than one year. Attach Schedule D (100S).
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 9 – Net IRC Section 1231 Gain (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the net IRC Section 1231 gain or loss from Schedule D-1.
                  Do not include casualty, theft, or specially allocated gains
                  and losses; report those on Line 10b. If the amount relates to
                  a passive activity, attach a statement identifying the
                  activity.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Other Income (Loss)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-k-other-income-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-k-other-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-k-other-income-img.png"
                    loading="lazy"
                    alt="Other Income (Loss)"
                    width={771}
                    height={70}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 10a – Other Portfolio Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s other portfolio income or loss not
                  reported on Lines 4 through 9. Attach a schedule listing each
                  item and amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 10b – Other Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s other income or loss not reported
                  elsewhere on Schedule K. Attach a schedule listing each item
                  and amount.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Deductions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-k-deductions-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-k-deductions-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-k-deductions-img.png"
                    loading="lazy"
                    alt="Deductions"
                    width={771}
                    height={130}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 11 – IRC Section 179 Expense Deduction
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s IRC Section 179 expense deduction for
                  eligible business property, such as machinery, equipment,
                  vehicles, furniture, computers, and qualifying business
                  software. Attach Schedule B (100S).
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation claimed a{' '}
                  <span style={{ fontWeight: 600 }}>$10,000</span> IRC Section
                  179 expense deduction for computer equipment and business
                  software.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12a – Cash Charitable Contributions
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of cash charitable contributions made
                  by the corporation in the tax year. Attach a schedule of the
                  amount and nature of each contribution.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation contributed{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> in cash
                  donations to qualified charities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12b – Noncash Charitable Contributions
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total noncash charitable contributions of the
                  corporation, such as donated property or goods. Attach a
                  schedule showing the amount and type of each contribution.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation donated office equipment valued at{' '}
                  <span style={{ fontWeight: 600 }}>$2,500</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12c – Investment Interest Expense
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s investment interest expense paid or
                  accrued on money borrowed to purchase or hold investment
                  property that produces portfolio income, such as interest,
                  dividends, annuities, royalties, etc. For more information,
                  see Form FTB 3526, Investment Interest Expense Deduction.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$1,200</span> in investment
                  interest expense related to investment securities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12d1 – IRC Section 59(e)(2) Expenditures
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body1">
                      Enter total deductions allocable to portfolio income or
                      loss (excluding interest expenses) and any qualified
                      expenditures that the corporation elects to amortize under
                      IRC Section 59(e)(2) rather than deduct currently.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Portfolio deductions are generally IRC Section 212
                      expenses subject to shareholder-level limitations, while
                      related interest expense must be reported separately on
                      Line 12b as investment interest expense.
                    </Typography>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12d2 – Type of Expenditures
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Specify the type of expenditures included in the amount
                  reported on Line 12d1.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> Research and
                  experimental expenses, intangible drilling costs, mining
                  exploration costs, or development expenditures.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12e – Deductions – Portfolio
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter deductions not reported elsewhere. Attach a schedule
                  showing how each deduction was computed and its basis. Include
                  California adjustments where required, such as adding back
                  disallowed penalties or excluding qualifying seismic incentive
                  amounts if treated differently for federal and state purposes.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Examples:</span>{' '}
                  Miscellaneous business expenses, allowable deductions not
                  listed on other lines, and non-deductible penalties for
                  California purposes.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12f – Other Deductions
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s deductions not reported on any other
                  line and attach a schedule showing how each deduction was
                  calculated and the basis for the deduction.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include California adjustments in column (c) for deductions
                  not allowed under California law, such as certain professional
                  sports league fines or penalties deducted for federal
                  purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$2,000</span> in other
                  business deductions.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Credits
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-k-credits-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-k-credits-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-k-credits-img.png"
                    loading="lazy"
                    alt="Credits"
                    width={771}
                    height={110}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 13a – Low-Income Housing Credit
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the corporation’s low-income housing credit for
                  qualified residential rental projects providing low-income
                  housing. Attach Form FTB 3521, Low-Income Housing Credit, if
                  applicable.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 13b – Credits Related to Rental Real Estate Activities
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter credits related to rental real estate activities, other
                  than the low-income housing credit. Attach a schedule showing
                  each credit amount and the related form.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 13c – Credits Related to Other Rental Activities
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter credits related to rental activities other than rental
                  real estate activities. Attach a schedule showing each credit
                  amount and the related form.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$900</span> in credits from
                  other rental activities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 13d – Other Credits
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter each shareholder’s share of credits related to trade or
                  business activities reported on Schedule C (100S), along with
                  an attached schedule detailing the credit type and amount.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  Examples of applicable credits include:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body1">
                      EZ Hiring Credit (FTB 3805Z)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      LAMBRA Hiring Credit (FTB 3807)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Manufacturing Enhancement Area (MEA) Hiring Credit (FTB
                      3808)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Targeted Tax Area (TTA) Hiring Credit (FTB 3809)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Research Credit (FTB 3523)
                    </Typography>
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The Pass-Through Entity (PTE) Elective Tax Credit must also be
                  reported on Line 13d with an attached schedule, even though it
                  is not a pass-through item.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 14 – Total Withholding Allocated to All Shareholders
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of withholding received by the
                  corporation (including withholding from other entities or
                  backup withholding) and allocate the amounts to shareholders
                  according to their percentage of ownership.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If a
                  corporation might receive{' '}
                  <span style={{ fontWeight: 600 }}>$5,000</span> of withholding
                  and a shareholder might own{' '}
                  <span style={{ fontWeight: 600 }}>25 percent</span> of the
                  stock. In that case that shareholder’s share of the
                  withholding would be{' '}
                  <span style={{ fontWeight: 600 }}>$1,250</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Alternative Minimum Tax (AMT) Items
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-k-alternative-minimum-tax-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-k-alternative-minimum-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-k-alternative-minimum-tax-img.png"
                    loading="lazy"
                    alt="Alternative Minimum Tax (AMT) Items
"
                    width={771}
                    height={110}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15a through Line 15f Instructions
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use Lines 15a–15f to report AMT adjustments and preference
                  items passed through to shareholders. Shareholders may need to
                  file Schedule P (540, 540NR, or 541).
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15a – Depreciation Adjustment on Property Placed in
                  Service After 12/31/86
                </Typography>

                <Box component="ul" sx={{ pl: 4, mb: 2 }}>
                  <li>
                    <Typography variant="body1">
                      Enter the difference between{' '}
                      <span style={{ fontWeight: 600 }}>
                        regular tax depreciation
                      </span>{' '}
                      and{' '}
                      <span style={{ fontWeight: 600 }}>AMT depreciation</span>{' '}
                      for tangible property placed in service after 1986.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Recompute depreciation using{' '}
                      <span style={{ fontWeight: 600 }}>
                        AMT rules and recovery periods
                      </span>
                      . Property placed in service after 1998. Recompute only
                      assets using the 200% declining balance method.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Do not include:</span>
                    </Typography>
                    <Box component="ul" sx={{ pl: 4 }}>
                      <li>
                        <Typography variant="body1">
                          Section 179 deduction
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body1">
                          Special depreciation allowance property
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body1">
                          Unit-of-production method property
                        </Typography>
                      </li>
                    </Box>
                  </li>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Subtract:</span> Regular
                      depreciation − AMT depreciation
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      If AMT depreciation is higher, enter the difference as a{' '}
                      <span style={{ fontWeight: 600 }}>negative amount</span>.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Include any related adjustment from{' '}
                      <span style={{ fontWeight: 600 }}>
                        depreciation capitalized to inventory
                      </span>
                      .
                    </Typography>
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15b – Adjusted Gain or Loss
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the difference between regular tax gain/loss and AMT
                  gain/loss when disposing of property placed in service after
                  1986. Recalculate using AMT adjusted basis.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> Sale of
                  machinery or building where AMT depreciation is different than
                  regular depreciation resulting in different gain.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15c – Depletion (Other Than Oil and Gas)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the adjustment for depletion deductions for mines, wells
                  (other than oil and gas), and natural resources. Recalculate
                  depletion under AMT rules and limits. Shareholders calculate
                  oil and gas depletion separately under Section 613A so it
                  should not be included.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> Depletion
                  from mining operations or mineral extraction (not oil or gas
                  wells).
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15d – Gross Income from Oil, Gas, and Geothermal
                  Properties
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total gross income from oil, gas, and geothermal
                  properties (Section 613(a)) received or accrued during the tax
                  year that is included in Form 100S, Page 1.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> Revenue from
                  the sale of crude oil or from the production of geothermal
                  energy.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15e – Deductions Allocable to Oil, Gas, and Geothermal
                  Properties
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter deductions allowed for AMT directly allocable to oil,
                  gas, and geothermal properties. Only expenses related to these
                  properties, recalculated under AMT rules, and separately
                  reported for oil/gas and geothermal.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> Operating
                  costs, extraction expenses, or production-related deductions
                  for oil or geothermal projects.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15f – Other AMT Items
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter all other AMT adjustment items not listed on Lines
                  15a–15e. Put a statement showing each item in parentheses.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Examples:</span> Accelerated
                  depreciation (pre-1987 rules), long-term contract adjustments,
                  passive activity losses, or tax shelter farm losses.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Items Affecting Shareholder Basis
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-k-item-affecting-shareholder-basis-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-k-item-affecting-shareholder-basis-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-k-item-affecting-shareholder-basis-img.png"
                    loading="lazy"
                    alt="Items Affecting Shareholder Basis
"
                    width={771}
                    height={100}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16a – Tax-Exempt Interest Income
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s tax-exempt interest income that
                  affects the shareholder’s stock basis.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$1,000</span> in tax-exempt
                  municipal bond interest.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16b – Other Tax-Exempt Income
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter other tax-exempt income not reported on Line 16a that
                  affects shareholder basis.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$500</span> in other
                  tax-exempt income.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16c – Non-deductible Expenses
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter non-deductible expenses that are not deductible for tax
                  purposes but reduce the shareholder’s stock basis.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16d – Total Property Distributions (Including Cash) Other
                  Than Dividends
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter total cash and property distributions made to
                  shareholders, excluding dividends reported on Line 17c.
                  Noncash property distributions should be reported at fair
                  market value.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation distributed{' '}
                  <span style={{ fontWeight: 600 }}>$8,000</span> in cash and
                  equipment to shareholders.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Other Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-k-other-information-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-k-other-information-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-k-other-information-img.png"
                    loading="lazy"
                    alt="Other Information"
                    width={771}
                    height={100}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 17a – Investment Income
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation's investment income reported on Schedule
                  K, such as interest, dividends, royalties, and other portfolio
                  income. Exclude income from passive activities.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation earned{' '}
                  <span style={{ fontWeight: 600 }}>$4,000</span> of dividends
                  and interest as investment income.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 17b – Investment Expenses
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter investment expenses directly related to producing
                  investment income, excluding investment interest expense
                  reported on Line 12c.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  For more information, see Form FTB 3526, Investment Interest
                  Expense Deduction.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$900</span> in investment
                  management expenses.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 17c – Total Dividend Distributions Paid from Accumulated
                  Earnings and Profits
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter dividend distributions paid out of accumulated earnings
                  and profits from prior C corporation years. Shareholders
                  should also receive a federal Form 1099-DIV showing their
                  share of distributions.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation paid{' '}
                  <span style={{ fontWeight: 600 }}>$6,000</span> in taxable
                  dividend distributions.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 17d – Other Items and Amounts Required to Be Reported
                  Separately to Shareholders
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Any other items or supplemental information that must be
                  reported separately to shareholders. Attach a schedule with
                  details and calculations, if any.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  These may include:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body1">
                      IRC Section 179 recapture information
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Installment sale information
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Gross receipts information
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Credit recapture amounts
                    </Typography>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation also included more information for an installment
                  sale reported to shareholders.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Other State Taxes
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-k-other-other-state-taxes-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-k-other-other-state-taxes-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-k-other-other-state-taxes-img.png"
                    loading="lazy"
                    alt="Other State Taxes
"
                    width={771}
                    height={90}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18a – Type of Income
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the type of income earned and taxed by another state.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18b – Name of State
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name of the state where the income was earned and
                  taxes were paid.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18c – Total Gross Income from Sources Outside California
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the corporation’s total gross income earned from sources
                  outside California. Attach a schedule if required.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18d – Total of Applicable Deductions and Losses
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter deductions and losses relating to income earned outside
                  of California. Attach a schedule if necessary.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> Deductions
                  allocated by the corporation were{' '}
                  <span style={{ fontWeight: 600 }}>$20,000</span>.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18e – Total Other State Taxes
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total taxes paid to other states. Check the box to
                  show whether the taxes were paid or accrued. Attach a copy of
                  the return filed with the other state, if any.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> The
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$3,500</span> of taxes paid
                  to Arizona and checked the{' '}
                  <span style={{ fontWeight: 600 }}>Paid</span> box.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Reconciliation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/100s-schedule-k-reconciliation-img.webp" />
                  <source srcSet="/assets/images/100s-schedule-k-reconciliation-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/100s-schedule-k-reconciliation-img.png"
                    loading="lazy"
                    alt="Reconciliation"
                    width={771}
                    height={70}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 19 – Reconciliation of Income (Loss)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the corporation’s final California income or loss by
                  adding the applicable income amounts from Lines 1, 2, and 3c
                  through 10b, then subtracting the allowable deductions from
                  Lines 11, 12a, 12b, 12c, 12d1, 12e, and 12f. The amount should
                  reconcile to{' '}
                  <span style={{ fontWeight: 600 }}>Schedule M-1, Line 8</span>{' '}
                  when Schedule M-1 is required.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> After
                  combining all income items and allowable deductions, the
                  corporation reported{' '}
                  <span style={{ fontWeight: 600 }}>$45,000</span> of California
                  income.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Common Form 100S Filing Mistakes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  These are common mistakes businesses make:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body1">
                      Failure to Pay Minimum Franchise Tax of $800
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Incorrect shareholder allocations
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Incomplete schedules filed
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Misallocations to shareholders
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Using incorrect apportionment techniques
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Final return submissions ignored
                    </Typography>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="maximum-shareholders-note"
                >
                  <b>Note :</b> Checking returns carefully before filing helps
                  avoid penalties and amended returns.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Penalties and Interest for Form 100S
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Penalties Table">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Type
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 500,
                              fontWeight: 600,
                            }}
                          >
                            How Much
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Late Filing Penalties
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            5% of the tax not paid for each month the return is
                            late, to a maximum of 25%
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Late Payment Penalties
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            5% of unpaid tax plus 0.5% each month the tax
                            remains unpaid, up to 25%
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Estimated Tax Underpayment Penalties
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Depending on the extent of underpayment and time of
                            nonpayment
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Electronic Payment (EFT) Penalties
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            10% of the amount not paid electronically
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Form 5471 / Form 8975 Penalty
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            $1,000 for each required form not filed
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Record Maintenance Penalties
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            $10,000 for each taxable year of noncompliance
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Accuracy-Related Penalties
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            20% of the underpaid tax
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Fraudulent Penalties
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            75% of the underpaid tax
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Interest on Unpaid Tax
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Interest accrues until the entire balance is paid
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="maximum-shareholders-note"
                >
                  <b>Note :</b> Penalties and interest may continue to increase
                  if the balance remains unpaid or required information is not
                  provided on time.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Documents Needed Before Filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  Prepare these documents before you file:
                </Typography>

                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                  <li>
                    <Typography variant="body1">Federal Form 1120-S</Typography>
                  </li>
                  <li>
                    <Typography variant="body1">Income statement</Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Statement of financial position (Balance sheet)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Shareholder lists of ownership
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">Payroll records</Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Prior-year California returns
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Estimated tax payment records
                    </Typography>
                  </li>
                </Box>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Note :</b> Maintaining organized financial records makes
                  filing easier.
                </Alert>

                <Typography
                  variant="h4"
                  sx={{ mb: 2, fontWeight: 600, color: '#124E78' }}
                >
                  Final Filing Tips
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body1">
                      File before the deadline to avoid penalties
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Review shareholder information carefully
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Attach all required schedules and forms
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Keep copies of filed returns and payment confirmations
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Verify California adjustments before submission
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Use e-filing for faster and secure filing
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      Keep your financial records accurate throughout the year
                    </Typography>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <b>Source and References</b>
                  <br /> For additional filing guidance and reporting
                  requirements, refer to the official California Form 100S{' '}
                  <Link
                    href="https://www.ftb.ca.gov/forms/2025/2025-100s-booklet.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    instructions
                  </Link>{' '}
                  and related FTB forms and publications.
                </Alert>

                {/* <!-----------------------------------------------------------> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="faq"
                >
                  Frequently Asked Questions (FAQs)
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. What is Form 100S California?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/business-tax/efile-california-form-100s/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 100S California
                  </Link>{' '}
                  <span style={{ fontWeight: 600 }}>Form 100S California</span>{' '}
                  is the official tax return filed by corporations that have
                  elected S corporation status in California. The form reports
                  California-source income, deductions, taxes, credits, and
                  shareholder allocations.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. What is the tax rate for California Form 100S?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Most California S corporations filing Form 100S pay a{' '}
                  <span style={{ fontWeight: 600 }}>
                    1.5% franchise or income tax
                  </span>{' '}
                  on their net income. Financial S corporations are taxed at a
                  higher rate of <span style={{ fontWeight: 600 }}>3.5%</span>.
                  California also generally requires S corporations to pay a
                  minimum{' '}
                  <span style={{ fontWeight: 600 }}>$800 franchise tax</span>,
                  even if the business has no income or operates at a loss.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. What is the difference between Form 100 and Form 100S?
                </Typography>

                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Form 100 vs Form 100S">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Features
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Form 100
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Form 100S
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Type of Corporation
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Filed by C-Corps
                          </TableCell>
                          <TableCell>Filed by S-Corps</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Taxation Method
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            The corporation pays taxes on its income
                          </TableCell>
                          <TableCell>
                            Income, deductions, and credits pass-through to
                            shareholders
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            California Tax Rate
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Generally taxed at <b>8.84%</b>
                          </TableCell>
                          <TableCell>
                            Generally taxed at <b>1.5%</b>
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Minimum Franchise Tax
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Minimum <b>$800</b> franchise tax (generally
                            applies)
                          </TableCell>
                          <TableCell>
                            Minimum <b>$800</b> franchise tax (generally
                            applies)
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Federal Election Required
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            No S corporation election needed
                          </TableCell>
                          <TableCell>
                            Must elect to be treated as an S corporation with
                            the IRS and California
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Main Purpose
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Report and pay California corporate income tax
                          </TableCell>
                          <TableCell>
                            Report California S corporation income, deductions,
                            credits, and shareholder allocations
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedules
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Standard corporate schedules
                          </TableCell>
                          <TableCell>
                            Includes Schedule K and Schedule K-1 for
                            shareholders
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. Where can I find California Form 100S instructions?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can find CA Form 100S instructions on the official
                  California Franchise Tax Board (FTB) website or on tax filing
                  platforms, such as TaxZeroone, which offers e-filing support
                  and a collection of tax forms for California businesses,
                  including Form 100S. Instructions include filing requirements,
                  schedules, and California adjustments with expert support.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  5. What is the California Franchise Tax Board (FTB)?
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body1">
                      The California Franchise Tax Board (FTB) is the state
                      agency that collects personal income and business taxes in
                      California. It administers and enforces state tax laws,
                      processes tax returns, issues refunds, and ensures
                      compliance for individuals, corporations, partnerships,
                      and S corporations.
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      In simple words, “What is Form 100S Franchise Tax Board”
                      is the California FTB form that S corporations use to file
                      their state tax return and comply with California tax
                      laws.
                    </Typography>
                  </li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File Form 100S with TaxZerone
            </Typography>

            <Box sx={{ maxWidth: 750, textAlign: 'center', m: 'auto' }}>
              <Typography
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 2,
                }}
              >
                E-file your CA S Corporation Franchise Tax Return (Form 100S)
                easily with TaxZerone to ensure accurate filing, timely
                compliance, and a smooth reporting process
              </Typography>
            </Box>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=100sinstructions`}
              >
                E-file now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form100SInstruction;
